import { memo, ChangeEvent, Dispatch, SetStateAction } from "react";

import { useFormContext } from "react-hook-form";
import { Box, Button, TextField } from "@mui/material";
import ImageDialog from "@app/components/ImageDialog";
import RenderNewUploadingImagesList from "@app/components/RenderNewUploadingImagesList";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRenderFileViewHook } from "@app/hooks/imageRender";
import { ApiEndpoint, imagesFileFormats } from "@constants/index";
import { useDeleteBlogInfoMutation } from "@modules/blog/api/blog.api";
import TextareaEditor from "common/Editor";

import { IFlowProcess } from "@pages/BlogMutation/useCreateBlogLogic";
import { CreateBlogState, ICreateBlogFormData } from "@pages/BlogMutation/types";
import { MainLayer, ContentWrapper, VisuallyHiddenInput, Fields } from "./styled";
import { blogFirstKey } from "@pages/BlogMutation/constant";

interface MultipleFormContentProps {
  id?: string;
  index: number;
  contentLength: number;
  isImageViewOpen: boolean;
  imageSrcForAction: string;
  isDeleteModalOpen?: boolean;
  setFlowProcess: Dispatch<SetStateAction<IFlowProcess>>;
  onChangeUpload: (event: ChangeEvent<HTMLInputElement>, index: number) => void;
}

const MultipleFormContent = ({
  id,
  index,
  contentLength,
  isImageViewOpen,
  imageSrcForAction,
  isDeleteModalOpen,
  setFlowProcess,
  onChangeUpload,
}: MultipleFormContentProps) => {
  const { setValue, watch, register, formState: { errors } } = useFormContext<ICreateBlogFormData>()
  const [deleteBlogInfo] = useDeleteBlogInfoMutation()
  const content = watch(blogFirstKey)
  const key = `${blogFirstKey}[${index}].` as const
  const image = watch(`${key}image` as `content.${number}.image`)
  const isUploadedImage = image && 'url' in image

  const previews = useRenderFileViewHook(image)

  const requestToDeleteBlogInfo = async () => {
    try {
      if (id) {
        await deleteBlogInfo({ id })
        setValue(blogFirstKey, [...content.filter((item) => item.id !== id)])

        return
      }

      const newArray = content.filter((_, i) => i !== index);

      setValue(blogFirstKey, newArray)
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <MainLayer>
      <ContentWrapper>
        <RenderNewUploadingImagesList<CreateBlogState>
          setState={setFlowProcess}
          newPreviewsList={isUploadedImage ? [`${ApiEndpoint}/${image.url}`] : previews}
          togetherImagesLength={isUploadedImage ? 1 : previews.length}
        />
        <ImageDialog
          url={imageSrcForAction}
          isOpen={isImageViewOpen}
          onClose={() => {
            setFlowProcess((prev) => ({ ...prev, isImageViewOpen: false }))
          }}
        />
        <Box
          sx={{ display: 'flex', gap: 1 }}
        >
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
          >
            <p>{ image ? 'Edit Image' : 'Upload Pictures' }</p>
            <VisuallyHiddenInput
              type="file"
              id="files"
              name="images"
              accept={imagesFileFormats.join(',')}
              onChange={(e) => onChangeUpload(e, index)}
            />
          </Button>
          {
          contentLength > 1 && (
            <Button
              variant="contained"
              onClick={() => requestToDeleteBlogInfo()}
            >
              Delete
            </Button>
          )
        }
        </Box>
        <Fields>
          <Box sx={{ width: '100%' }}>
            <TextareaEditor
              value={watch(`${key}descriptionAm` as `content.${number}.descriptionAm`)}
              label="Description Am"
              name={`${key}descriptionAm`}
              setValue={setValue}
              error={errors?.[blogFirstKey]?.[index]?.descriptionAm?.message}
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <TextareaEditor
              value={watch(`${key}descriptionEn` as `content.${number}.descriptionEn`)}
              label="Description En"
              name={`${key}descriptionEn`}
              setValue={setValue}
              error={errors?.[blogFirstKey]?.[index]?.descriptionEn?.message}
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <TextareaEditor
              value={watch(`${key}descriptionRu` as `content.${number}.descriptionRu`)}
              label="Description Ru"
              name={`${key}descriptionRu`}
              setValue={setValue}
              error={errors?.[blogFirstKey]?.[index]?.descriptionRu?.message}
            />
          </Box>

        </Fields>
      </ContentWrapper>
    </MainLayer>
  )
}

export default memo(MultipleFormContent)
